import { useEffect, useRef } from "react";

export default function MeshCanvas({ className = "", density = 70 }) {
  const ref = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = canvas.getContext("2d");
    let w, h, raf;
    let nodes = [];

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = canvas.width = rect.width * devicePixelRatio;
      h = canvas.height = rect.height * devicePixelRatio;
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      nodes = Array.from({ length: density }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.18 * devicePixelRatio,
        vy: (Math.random() - 0.5) * 0.18 * devicePixelRatio,
        r: (Math.random() * 1.4 + 0.8) * devicePixelRatio,
        hot: Math.random() < 0.12,
      }));
    };

    const onMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
    };

    const LINK = 130 * devicePixelRatio;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const px = (mouse.current.x - 0.5) * 24 * devicePixelRatio;
      const py = (mouse.current.y - 0.5) * 24 * devicePixelRatio;
      for (const n of nodes) {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }
      ctx.lineWidth = devicePixelRatio * 0.5;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK) {
            const o = (1 - d / LINK) * 0.3;
            ctx.strokeStyle = a.hot || b.hot ? `rgba(111,168,44,${o + 0.15})` : `rgba(47,107,60,${o})`;
            ctx.beginPath();
            ctx.moveTo(a.x + px * 0.4, a.y + py * 0.4);
            ctx.lineTo(b.x + px * 0.4, b.y + py * 0.4);
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        ctx.fillStyle = n.hot ? "rgba(111,168,44,0.9)" : "rgba(127,140,141,0.55)";
        ctx.beginPath();
        ctx.arc(n.x + px * 0.4, n.y + py * 0.4, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouse);
    if (!reduced) raf = requestAnimationFrame(draw);
    else draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, [density]);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
